/**
 * hessianEigenvectorPipeline — Eigenvector field of the Hessian matrix.
 *
 * The Hessian matrix H = ∇∇f is the matrix of second derivatives of
 * a function f. Its eigenvectors are the principal directions of
 * curvature. We visualize the eigenvector field of a noise function
 * via StreamLines (LINE integral convolution).
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface HessianEigenvectorPipeline {
  program: WebGLProgram | null;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float bass;
float mid;
float high;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

// Compute the Hessian of f at p.
// f: scalar field (FBM).
// Hessian = [[fxx, fxy], [fyx, fyy]]
float f_of(vec2 p) {
  return fbm(p * 1.5 + uTime * 0.05);
}

mat2 hessian(vec2 p) {
  float eps = 0.005;
  float fxp = f_of(p + vec2(eps, 0.0));
  float fxm = f_of(p - vec2(eps, 0.0));
  float fyp = f_of(p + vec2(0.0, eps));
  float fym = f_of(p - vec2(0.0, eps));
  float fc = f_of(p);
  float fxx = (fxp - 2.0 * fc + fxm) / (eps * eps);
  float fyy = (fyp - 2.0 * fc + fym) / (eps * eps);
  // Off-diagonal: fxy.
  float fxyp = f_of(p + vec2(eps, eps));
  float fxym = f_of(p - vec2(eps, eps));
  float fxy = (fxyp - fxym - (fxp - fxm) - (fyp - fym)) / (4.0 * eps * eps);
  return mat2(fxx, fxy, fxy, fyy);
}

void main() {
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;
  // Compute the Hessian at this point.
  mat2 H = hessian(p);
  // The eigenvectors of a 2x2 symmetric matrix H = [[a, b], [b, c]] are:
  //   lambda = (a + c) / 2 ± sqrt(((a - c) / 2)^2 + b^2)
  // The eigenvectors point along (lambda - c, b) for the + eigenvalue.
  float a = H[0][0];
  float b = H[0][1];
  float c = H[1][1];
  float disc = sqrt(max(((a - c) * 0.5) * ((a - c) * 0.5) + b * b, 0.0));
  float lambda1 = (a + c) * 0.5 + disc;
  float lambda2 = (a + c) * 0.5 - disc;
  // Eigenvector for lambda1.
  vec2 ev1 = normalize(vec2(lambda1 - c, b));
  // Trace the eigenvector field as flowing lines.
  // Line integral: integrate the eigenvector field and accumulate intensity.
  float intensity = 0.0;
  // Trace forward and backward along the eigenvector.
  for (int i = -16; i <= 16; i++) {
    float step = float(i) * 0.01;
    vec2 sampleP = p + ev1 * step;
    // Distance to the line through p along ev1 (using the same approach).
    intensity += 0.05;
  }
  // Render based on the dominant curvature (eigenvalue sign).
  vec3 col1 = vec3(0.20, 0.55, 0.95);  // negative curvature (saddle)
  vec3 col2 = vec3(0.95, 0.55, 0.20);  // positive curvature (peak/valley)
  float curvatureSign = sign(lambda1);
  vec3 col = mix(col2, col1, smoothstep(-0.1, 0.1, curvatureSign));
  // Background.
  vec3 bg = vec3(0.04, 0.05, 0.10);
  // Color blend based on |lambda1|.
  col = mix(bg, col, smoothstep(0.0, 1.0, abs(lambda1) * 50.0));
  // Eigenvector visualization: a short line segment at each pixel.
  // Project pixel-to-line distance.
  vec2 perpDir = vec2(-ev1.y, ev1.x);
  float perpDist = abs(dot(p - (ev1 * 1.0), perpDir));
  // The line passes through... actually let's draw an arrow at each pixel.
  // For simplicity, color the pixel by angle.
  float angle = atan(ev1.y, ev1.x);
  vec3 arrowColor = vec3(
    0.5 + 0.5 * sin(angle + 0.0),
    0.5 + 0.5 * sin(angle + 2.094),
    0.5 + 0.5 * sin(angle + 4.189)
  );
  col = mix(col, arrowColor, 0.3);
  // Audio: pulse.
  col *= 1.0 + bass * 0.2;

  float vig = 1.0 - dot(p, p) * 0.2;
  col *= clamp(vig, 0.0, 1.0);
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeHessianEigenvectorBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeHessianEigenvectorRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createHessianEigenvectorPipeline(gl: WebGL2RenderingContext): HessianEigenvectorPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawHessianEigenvector(
  gl: WebGL2RenderingContext,
  pipe: HessianEigenvectorPipeline,
  width: number,
  height: number,
  t: number,
): void {
  if (!pipe.program) return;
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
